import React, { useState } from 'react';
import { Input, InputNumber, Button, Modal, Spin } from 'antd';
import { useBonusDefForm } from './hooks/useBonusDefForm';

const P9133Form: React.FC = () => {
  const {
    mode, isEditing, loading,
    records, current, currentIdx,
    code, setCode,
    name, setName,
    date, setDate,
    memo, setMemo,
    amount, setAmount,
    taxRate, setTaxRate,
    navFirst, navPrev, navNext, navLast,
    startAdd, startEdit, cancel, save,
    doDelete, quickSearch,
  } = useBonusDefForm();

  const [searchText, setSearchText] = useState('');

  const handleDelete = () => {
    if (!current) return;
    Modal.confirm({
      title: '確認刪除',
      content: `確定要刪除「${current.f933_01} ${current.f933_11}」？`,
      okText: '刪除',
      okType: 'danger',
      cancelText: '取消',
      onOk: doDelete,
    });
  };

  const handleQuickSearch = () => {
    if (searchText.trim()) {
      quickSearch(searchText.trim());
    }
  };

  const modeLabel = mode === 'add' ? '新增' : mode === 'edit' ? '修改' : '瀏覽';

  return (
    <Spin spinning={loading}>
      <div className="max-w-2xl mx-auto p-6">
        {/* Title */}
        <h2 className="text-xl font-bold mb-4">
          獎金發放定義 (pw9133f1)
          <span className="ml-3 text-sm font-normal text-gray-500">
            [{modeLabel}] {records.length > 0 ? `${currentIdx + 1} / ${records.length}` : '無資料'}
          </span>
        </h2>

        {/* Quick Search Bar */}
        <div className="flex items-center gap-2 mb-4">
          <Input
            placeholder="輸入代號快速查詢"
            value={searchText}
            onChange={e => setSearchText(e.target.value)}
            onPressEnter={handleQuickSearch}
            disabled={isEditing}
            style={{ width: 300 }}
            maxLength={10}
          />
          <Button onClick={handleQuickSearch} disabled={isEditing}>查詢</Button>
        </div>

        {/* Navigation Bar */}
        <div className="flex items-center gap-1 mb-4">
          <Button size="small" onClick={navFirst} disabled={isEditing || records.length === 0}>|&lt;</Button>
          <Button size="small" onClick={navPrev} disabled={isEditing || currentIdx <= 0}>&lt;</Button>
          <Button size="small" onClick={navNext} disabled={isEditing || currentIdx >= records.length - 1}>&gt;</Button>
          <Button size="small" onClick={navLast} disabled={isEditing || records.length === 0}>&gt;|</Button>
        </div>

        {/* Form Fields */}
        <div className="space-y-4 mb-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">薪資發放代號 (F933_01)</label>
            <Input
              value={code}
              onChange={e => setCode(e.target.value)}
              maxLength={10}
              disabled={!isEditing || mode === 'edit'}
              style={{ width: 200 }}
              placeholder="最多10字"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">發放名稱 (F933_11)</label>
            <Input
              value={name}
              onChange={e => setName(e.target.value)}
              maxLength={20}
              disabled={!isEditing}
              style={{ width: 300 }}
              placeholder="最多20字"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">發放日期 (F933_12)</label>
            <Input
              type="date"
              value={date}
              onChange={e => setDate(e.target.value)}
              disabled={!isEditing}
              style={{ width: 200 }}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">稅率 % (F933_22)</label>
            <InputNumber
              value={taxRate}
              onChange={v => setTaxRate(v ?? 0)}
              min={0}
              max={100}
              step={0.1}
              disabled={!isEditing}
              style={{ width: 120 }}
              precision={1}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">備註 (F933_13)</label>
            <Input
              value={memo}
              onChange={e => setMemo(e.target.value)}
              maxLength={60}
              disabled={!isEditing}
              style={{ width: 500 }}
              placeholder="最多60字"
            />
          </div>
        </div>

        {/* Audit Fields (read-only) */}
        {current && mode === 'browse' && (
          <div className="text-xs text-gray-400 mb-6 space-y-1">
            <div>建立人員: {current.f933_91} | 建立日期: {current.f933_92 || '-'}</div>
            <div>修改人員: {current.f933_93}</div>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex items-center gap-2">
          {!isEditing ? (
            <>
              <Button type="primary" onClick={startAdd}>新增</Button>
              <Button onClick={startEdit} disabled={!current}>修改</Button>
              <Button danger onClick={handleDelete} disabled={!current}>刪除</Button>
            </>
          ) : (
            <>
              <Button type="primary" onClick={save}>儲存</Button>
              <Button onClick={cancel}>取消</Button>
            </>
          )}
        </div>
      </div>
    </Spin>
  );
};

export default P9133Form;
